import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Card from "../Generic/Card";
import Dropdown from "../Generic/Dropdown";
import ToggleSwitch from "../Generic/ToggleSwitch";
import FoodMakingService from "./Services/FoodMakingService";
import IotService from "./Services/iotService";
import { LoadingToast, UpdateToastFailed, UpdateToastSuccess, errorNotification, successNotification } from "../Generic/Notifications";
import Button from "../Generic/Button";
import { bindActionCreators } from "redux";
import { setMachineID } from "../Landing/Actions";
import * as Actions from "./Actions";
import { connect } from "react-redux";
import Loader from "../Generic/Loader";
import { isEmpty } from "../util/util";
import EmptyState from "../Generic/EmptyState";
import { Table, Thead, Trow, Tcol, Tbody } from "../Generic/Table";
import { formatDisplayDate } from "../util/date.util";
import { Link } from "react-router-dom";
import { AppRoutes, DashboardModules, StudioSubModules } from "../config/app.config";

const Page = styled.div`
	height: max-content;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: left;
	padding: 2rem;
	gap:1rem;
	@media (max-width: 1000px) {
		padding: 2rem;
	}
`;

const Header = styled.div`
	display: flex;
	font-size: 26px;
	margin: 0 0 0 0.5rem;
`;

const SpacedOutRow = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	width: 100%;
	height: max-content;
`;

const Row = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-start;
	gap: ${(props) => props.gap};
`;

const PrimaryText = styled.div`
	color: rgba(0, 0, 0, 1);
	font-size: 18px;
	font-weight: 600;
	margin: ${(props) => props.margin};
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	flex-wrap: ${(props) => props.flexWrap};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
`;

const Span = styled.span`
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	color: ${(props) => props.color};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
`;

/** Live Menu card: title → menu name → submenu → description (most → least prominent) */
const LiveMenuTitle = styled.h2`
	margin: 0;
	font-size: 1.75rem;
	font-weight: 700;
	letter-spacing: -0.02em;
	color: rgba(0, 0, 0, 0.95);
	line-height: 1.2;
`;

const LiveMenuMenuName = styled.div`
	margin: 0.4rem 0 0 0;
	font-size: 1.125rem;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.82);
	line-height: 1.35;
`;

const SubMenuHeading = styled.div`
	margin: 0;
	font-size: 1rem;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.88);
	line-height: 1.4;
`;

const SubMenuDescriptionText = styled.div`
	font-size: 0.875rem;
	font-weight: 400;
	color: rgba(0, 0, 0, 0.52);
	line-height: 1.45;
`;

const Pagelink = styled(Link).attrs({
	target: "_blank",
	rel: "noopener noreferrer", //Open in new tab
})`
	text-decoration: underline;
	cursor: pointer;
	&:hover {
		text-decoration: underline;
	}
	color: #1565c0 !important;
`;

const Overlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.1);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 310;
`;

const CenteredContainer = styled.div`
	width: 100%;
	height: max-content;
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: ${(props) => props.margin};
`;

const TASKS = {
	"PRIME_PREPARE": "prime_prepare"
}

const COMPONENTS = {
	PAGE: "PAGE",
	RESTAURANT_STATUS: "RESTAURANT_STATUS",
	RESTAURANT_UPDATE: "RESTAURANT_UPDATE",
	NOTIFICATIONS_STATUS: "NOTIFICATIONS_STATUS",
	LIVE_MENU: "LIVE_MENU",
};

const COMPONENT_STATES = {
	LOADING: "LOADING",
	ERROR: "ERROR",
	OK: "OK",
};

const INITIAL_COMPONENT_STATES = {
	[COMPONENTS.PAGE]: COMPONENT_STATES.OK,
	[COMPONENTS.RESTAURANT_STATUS]: COMPONENT_STATES.LOADING,
	[COMPONENTS.RESTAURANT_UPDATE]: COMPONENT_STATES.OK,
	[COMPONENTS.NOTIFICATIONS_STATUS]: COMPONENT_STATES.LOADING,
	[COMPONENTS.LIVE_MENU]: COMPONENT_STATES.LOADING,
};

/** Hour:minute only for schedule range display */
const SCHEDULE_TIME_FORMAT = "HH:mm";

function formatScheduleRange(startDate, endDate) {
	const start = formatDisplayDate(startDate, SCHEDULE_TIME_FORMAT);
	const end = formatDisplayDate(endDate, SCHEDULE_TIME_FORMAT);
	if (start === "-" || end === "-") {
		return "-";
	}
	return `${start} to ${end}`;
}

// Backend stores schedule time as {h, m}; convert to a Date so the existing
// formatScheduleRange helper can render it via formatDisplayDate.
function timeToDate(time) {
	if (!time || typeof time.h !== "number" || typeof time.m !== "number") {
		return null;
	}
	const date = new Date();
	date.setHours(time.h, time.m, 0, 0);
	return date;
}

function transformMenuData(menuResponse) {
	const menuData = menuResponse?.menuData ?? {};
	const recipeIdNameMap = menuResponse?.recipeIdNameMap ?? {};
	const dishNameMap = menuResponse?.dishNameMap ?? {};
	const dishesMap = menuData?.dishes ?? {};
	const servingSchedules = menuData?.servingSchedules ?? {};
	const subMenus = Array.isArray(menuData?.subMenus) ? menuData.subMenus : [];

	const transformedSubMenus = subMenus.map((subMenu) => {
		const dishIds = Array.isArray(subMenu?.dishIds) ? subMenu.dishIds : [];
		const dishes = dishIds.map((dishId) => {
			const dish = dishesMap?.[dishId] ?? {};
			const schedule = servingSchedules?.[dish?.servingScheduleId];
			const firstSlot = schedule?.schedules?.[0]?.timeSlots?.[0];
			return {
				dishId,
				dishName: dishNameMap?.[dishId] ?? "-",
				recipeId: dish?.recipeId,
				recipeName: recipeIdNameMap?.[dish?.recipeId] ?? "-",
				startDate: timeToDate(firstSlot?.start),
				endDate: timeToDate(firstSlot?.end),
			};
		});
		return {
			name: subMenu?.name,
			description: subMenu?.description,
			dishes,
		};
	});

	return { name: menuData?.name, subMenus: transformedSubMenus };
}

const FoodMaking = (props) => {
	//States and Functions of Redux declared for use
	const { machineID, kitchenId, restaurant, setRestaurantData, RestaurantServingStatusesMap, RestaurantServingStatuses } = props;
	const [restoStateToSet, setRestoStateToSet] = useState(null);
	const [notificationsData, setNotificationsData] = useState(null);
	const [liveMenu, setLiveMenu] = useState(null);
	const [componentStates, changeComponentStates] = useState(INITIAL_COMPONENT_STATES);
	const restaurantStatusOptions = RestaurantServingStatuses ? Object.entries(RestaurantServingStatuses).map((item) => ({
		head: item[0],
		value: item[1],
	})) : [];

	async function updateResturantStatus() {
		if (restoStateToSet === null) {
			errorNotification("No restaurant status selected");
			return;
		}
		changeComponentStates((prev) => ({ ...prev, [COMPONENTS.PAGE]: COMPONENT_STATES.LOADING }));
		const { data, error } = await FoodMakingService.changeRestaurantServingStatus(machineID, { servingStatus: restoStateToSet });
		changeComponentStates((prev) => ({ ...prev, [COMPONENTS.PAGE]: COMPONENT_STATES.OK }));
		if (data) {
			const { erroredRestaurants } = data || {};
			if (isEmpty(erroredRestaurants)) {
				successNotification(`Restaurant Status set to ${RestaurantServingStatusesMap[restoStateToSet]}`);
				setRestoStateToSet(null);
				getRestaurantStatus(true);
			} else {
				const { activeOrders } = erroredRestaurants?.[machineID] || {}
				if (activeOrders) {
					errorNotification(`Could not change Restaurant Status to ${RestaurantServingStatusesMap[restoStateToSet]} as the Restaurant has Active Orders`);
				} else {
					errorNotification(`Could not change Restaurant Status to ${RestaurantServingStatusesMap[restoStateToSet]}`);
				}
			}
		}
		if (error) {
			errorNotification(error);
		}
	}

	async function sendPrimePrepareTask() {
		const PrimePrepareTask = "prime_prepare";
		const toastId = LoadingToast(`Sending Prime Prepare Task`);
		const { data, error } = await IotService.sendTask(kitchenId, machineID, PrimePrepareTask);
		if (data) {
			UpdateToastSuccess('Prime Prepare task sent', toastId);
		}
		if (error) {
			UpdateToastFailed('Prime Prepare task failed to send', toastId);
		}
	}

	async function getRestaurantStatus(handleLoading = false) {
		if (handleLoading) changeComponentStates((prev) => ({ ...prev, [COMPONENTS.RESTAURANT_STATUS]: COMPONENT_STATES.LOADING }));
		const { data, error } = await FoodMakingService.getRestaurantData(machineID);
		if (handleLoading) changeComponentStates((prev) => ({ ...prev, [COMPONENTS.RESTAURANT_STATUS]: COMPONENT_STATES.OK }));
		if (data) {
			setRestaurantData({ ...data })
		}
		if (error) {
			changeComponentStates((prev) => ({ ...prev, [COMPONENTS.RESTAURANT_STATUS]: COMPONENT_STATES.ERROR }));
			errorNotification(error);
		}
	}

	async function getLiveMenu(handleLoading = false) {
		if (handleLoading) changeComponentStates((prev) => ({ ...prev, [COMPONENTS.LIVE_MENU]: COMPONENT_STATES.LOADING }));
		const { data, error } = await FoodMakingService.getMenuData(machineID);
		if (data) {
			setLiveMenu(transformMenuData(data));
			changeComponentStates((prev) => ({ ...prev, [COMPONENTS.LIVE_MENU]: COMPONENT_STATES.OK }));
		}
		if (error) {
			changeComponentStates((prev) => ({ ...prev, [COMPONENTS.LIVE_MENU]: COMPONENT_STATES.ERROR }));
		}
	}

	async function getNotificationsStatusForRestaurant(handleLoading = false) {
		if (handleLoading) changeComponentStates((prev) => ({ ...prev, [COMPONENTS.NOTIFICATIONS_STATUS]: COMPONENT_STATES.LOADING }));
		const { data, error } = await FoodMakingService.getNotificationsStatus(machineID);
		if (handleLoading) changeComponentStates((prev) => ({ ...prev, [COMPONENTS.NOTIFICATIONS_STATUS]: COMPONENT_STATES.OK }));
		if (data) {
			setNotificationsData({ ...data });
		}
		if (error) {
			changeComponentStates((prev) => ({ ...prev, [COMPONENTS.NOTIFICATIONS_STATUS]: COMPONENT_STATES.ERROR }));
		}
	}

	async function updateNotificationsStatus(notificationStatus = false) {
		const { data, error } = await FoodMakingService.updateNotificationsStatus(machineID, { notificationStatus });
		if (data) {
			successNotification(notificationStatus ? "Notifications enabled" : "Notifications disabled");
			setNotificationsData((prev) => ({ ...prev, ...data }));
			return true;
		}
		if (error) {
			errorNotification(error);
			return false;
		}
		return false;
	}

	useEffect(() => {
		getRestaurantStatus(true);
		getNotificationsStatusForRestaurant(true);
		getLiveMenu(true);
	}, []);

	function renderRestaurantStatus(data, componentState) {
		let renderData = [];

		switch (componentState) {
			case COMPONENT_STATES.LOADING:
				renderData.push(
					<CenteredContainer>
						<Loader />
					</CenteredContainer>
				);
				break;

			case COMPONENT_STATES.ERROR:
				renderData.push(<CenteredContainer>Failed to Restaurant Status</CenteredContainer>);
				break;

			case COMPONENT_STATES.OK:

				const servingStatus = RestaurantServingStatusesMap?.[data?.servingStatus] || "-";

				renderData.push(
					<Row gap="1rem 2rem">
						<Row gap="1rem">
							<PrimaryText>Serving Status</PrimaryText>- <PrimaryText>{servingStatus}</PrimaryText>
						</Row>
					</Row>
				);
				break;

			default:
				break;
		}

		return (
			<Card jContent="" alignItems="" padding="1.5rem" gap="2rem" width="max-content" margin="0 0 1rem 0">
				{renderData}
			</Card>
		);
	}

	function renderNotificationsStatus(componentState) {
		let renderData = [];

		switch (componentState) {
			case COMPONENT_STATES.LOADING:
				renderData.push(
					<CenteredContainer key="loading">
						<Loader />
					</CenteredContainer>
				);
				break;

			case COMPONENT_STATES.ERROR:
				renderData.push(
					<EmptyState message="Failed to load notifications status" retryButtonText="Retry" retry={() => getNotificationsStatusForRestaurant(true)} />
				);
				break;

			case COMPONENT_STATES.OK:
				renderData.push(
					<Row key={`notifications-status`} gap="1rem 2rem" style={{ flexWrap: "wrap", alignItems: "center" }}>
						<ToggleSwitch
							key={`notifications-toggle`}
							title="Notifications"
							initialStatus={notificationsData?.notificationStatus ?? false}
							onChange={async (newStatus) => updateNotificationsStatus(newStatus)}
							isAsync={true}
						/>
					</Row>
				);
				break;

			default:
				break;
		}

		return (
			<Card jContent="" alignItems="" padding="1.5rem" gap="2rem" width="max-content" margin="0 0 1rem 0">
				{renderData}
			</Card>
		);
	}

	function renderLiveMenu(liveMenu, componentState) {
		const subMenus = liveMenu?.subMenus ?? [];
		let body = null;

		switch (componentState) {
			case COMPONENT_STATES.LOADING:
				body = (
					<CenteredContainer>
						<Loader />
					</CenteredContainer>
				);
				break;
			case COMPONENT_STATES.ERROR:
				body = (
					<EmptyState
						message="Failed to fetch live menu"
						retryButtonText="Retry"
						retry={() => getLiveMenu(true)}
					/>
				);
				break;
			case COMPONENT_STATES.OK:
				if (subMenus.length === 0) {
					body = <Span color="rgba(0,0,0,0.6)">No Submenus found for menu.</Span>;
				} else {
					body = subMenus.map((subMenu, idx) => (
						<Row
							key={subMenu.name ? `${subMenu.name}-${idx}` : `submenu-${idx}`}
							gap="0.1rem"
							style={{ flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
							<SubMenuHeading>{subMenu.name ?? "-"}</SubMenuHeading>
							<SubMenuDescriptionText>{subMenu.description ?? "-"}</SubMenuDescriptionText>
							<Table margin="0.5rem 0 0 0">
								<Trow head top="0">
									<Thead>Dish Name</Thead>
									<Thead>Recipe Name</Thead>
									<Thead>Schedule</Thead>
								</Trow>
								<Tbody>
									{subMenu?.dishes?.length ? subMenu.dishes.map((dish, dishIndex) => (
										<Trow hoverEffect={false} key={dish.dishId != null ? `${dish.dishId}-${dishIndex}` : dish.dishName ? `${dish.dishName}-${dishIndex}` : `dish-${dishIndex}`}>
											<Tcol title="Dish Name" borderRadius="10px 0 0 10px">
												<Div flexDirection="column" gap="0.5rem">
												{dish.dishName ?? "-"}
												{dish.dishId ? (
													<Pagelink
														to={AppRoutes[DashboardModules.STUDIO][StudioSubModules.DISH].DISH_DETAILS.getPath(dish.dishId)}>
														View Dish
													</Pagelink>
												) : null}
												</Div>
											</Tcol>
											<Tcol title="Recipe Name">
												<Div flexDirection="column" gap="0.5rem">
												{dish.recipeName ?? "-"}
												{dish.recipeId ? (
													<Pagelink
														to={AppRoutes[DashboardModules.STUDIO][StudioSubModules.RECIPE].RECIPE_DETAILS.getPath(dish.recipeId)}>
														View recipe
													</Pagelink>
												) : null}
												</Div>
											</Tcol>
											<Tcol title="Schedule" borderRadius="0 10px 10px 0">
												{formatScheduleRange(dish.startDate, dish.endDate)}
											</Tcol>
										</Trow>
									)) : <Trow>
										<Tcol borderRadius="10px" colSpan={3}>
											<Div txtAlign="left">No dishes</Div>
										</Tcol>
									</Trow>}
								</Tbody>
							</Table>
						</Row>
					));
				}
				break;
			default:
				body = null;
		}

		return (
			<Card
				height="max-content"
				alignItems="flex-start"
				jContent="flex-start"
				padding="1.5rem"
				gap="1.5rem"
				width="100%"
				margin="1rem 0 1rem 0"
				overflowX="auto">
				<LiveMenuTitle>Live Menu</LiveMenuTitle>
					{componentState === COMPONENT_STATES.OK && liveMenu?.name ? (
						<LiveMenuMenuName>{liveMenu.name}</LiveMenuMenuName>
					) : null}
				{body}
			</Card>
		);
	}

	return (
		<Page>
			{componentStates[COMPONENTS.PAGE] === COMPONENT_STATES.LOADING ? <Overlay /> : ""}
			<Header>Restaurant Data</Header>
			{renderRestaurantStatus(restaurant, componentStates[COMPONENTS.RESTAURANT_STATUS])}

			<Header>Actions</Header>
			<Card
				gap="1rem"
				padding="1rem"
				jContent="center"
				width="90%"
				widthAfter1000="max-content"
				flexWrap="wrap"
				flexDirection="row">
				<Div>
					<Dropdown
						height="100%"
						widthVal="16rem"
						head="Set Serving State"
						optionsList={restaurantStatusOptions}
						value={restoStateToSet}
						onChange={setRestoStateToSet}
					/>
					&nbsp;
					<Button onClick={updateResturantStatus}>Set</Button>
				</Div>

				<Button onClick={sendPrimePrepareTask}>Prime Prepare</Button>
			</Card>

			<Header>Feature Flags</Header>
			{renderNotificationsStatus(componentStates[COMPONENTS.NOTIFICATIONS_STATUS])}

			{renderLiveMenu(liveMenu, componentStates[COMPONENTS.LIVE_MENU])}
		</Page>
	);
};

const mapStatetoProps = (state) => {
	const { foodmaking, landing } = state;
	return {
		machineID: landing.machineID,
		kitchenId: landing.kitchenId,
		...foodmaking,
	};
};

const mapDispatchToProps = (dispatch) => ({
	...bindActionCreators(
		{
			setMachineID: setMachineID,
			...Actions,
		},
		dispatch
	),
});

export default connect(mapStatetoProps, mapDispatchToProps)(FoodMaking);
